/**
 * PickupQueueCard — valet staff view of open resident pickup requests for
 * their assigned properties, with Mark-as-Completed + optional note.
 */
import { useCallback, useEffect, useState } from 'react';
import { Trash2, Recycle, RefreshCw, CheckCircle2 } from 'lucide-react';
import { apiFetch } from '../lib/apiFetch';
import { toast } from 'sonner';

const GOLD = '#C9A962';

function PickupRow({ r, onDone }) {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const Icon = r.kind === 'recycling' ? Recycle : Trash2;

  const complete = async () => {
    setBusy(true);
    try {
      await apiFetch(`/api/resident/staff/pickups/${r.request_id}/complete`,
        { method: 'PATCH', body: { note: note || undefined } });
      toast.success(`Unit ${r.unit_number} ${r.kind} pickup completed`);
      onDone();
    } catch (e) { toast.error(e.detail || 'Complete failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-lg border border-zinc-700 bg-zinc-800/40 p-3" data-testid={`pq-row-${r.request_id}`}>
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div className="flex items-start gap-2.5 min-w-0">
          <Icon className="h-4 w-4 mt-0.5 shrink-0" style={{ color: r.kind === 'recycling' ? '#34d399' : GOLD }} />
          <div className="min-w-0">
            <p className="text-xs font-bold text-white">
              {r.kind === 'recycling' ? 'Recycling' : 'Trash'} · Unit {r.unit_number}
              <span className={`ml-2 px-1.5 py-0.5 rounded text-[9px] font-black uppercase ${r.source === 'qr_scan' ? 'bg-purple-950/60 text-purple-400' : 'bg-blue-950/60 text-blue-400'}`}>
                {r.source === 'qr_scan' ? 'QR Scan' : 'Manual'}
              </span>
            </p>
            <p className="text-[10px] text-zinc-500 mt-0.5">
              {r.property_name} · {r.resident_name}
              {r.location ? ` · ${r.location}` : ''}{r.preferred_time ? ` · pref ${r.preferred_time}` : ''}
              {' · '}{new Date(r.created_at).toLocaleString()}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <input placeholder="Completion note (optional)" value={note} onChange={(e) => setNote(e.target.value)}
            className="rounded bg-zinc-900 border border-zinc-600 px-2 py-1.5 text-white text-[11px] w-44"
            data-testid={`pq-note-${r.request_id}`} />
          <button onClick={complete} disabled={busy}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[11px] font-bold disabled:opacity-50"
            style={{ backgroundColor: GOLD, color: '#1a1a1a' }} data-testid={`pq-complete-${r.request_id}`}>
            <CheckCircle2 className="h-3.5 w-3.5" /> Mark Completed
          </button>
        </div>
      </div>
    </div>
  );
}

export default function PickupQueueCard() {
  const [rows, setRows] = useState(null);

  const load = useCallback(async () => {
    try {
      const d = await apiFetch('/api/resident/staff/pickups');
      setRows(d.requests || []);
    } catch (_) { setRows(null); }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 45000);
    return () => clearInterval(t);
  }, [load]);

  if (rows === null) return null;

  return (
    <div className="rounded-xl border border-zinc-700 bg-zinc-900/70 p-4" data-testid="pickup-queue-card">
      <div className="flex items-center justify-between mb-2">
        <h3 className="flex items-center gap-2 text-xs font-black uppercase tracking-[0.2em]" style={{ color: GOLD }}>
          <Trash2 className="h-4 w-4" /> Resident Pickup Requests · {rows.length} open
        </h3>
        <button onClick={load} className="p-1.5 rounded-full border border-zinc-600 text-zinc-300" data-testid="pq-refresh-btn">
          <RefreshCw className="h-3.5 w-3.5" />
        </button>
      </div>
      {rows.length === 0 ? (
        <p className="text-[11px] text-zinc-500">No open pickup requests for your assigned properties.</p>
      ) : (
        <div className="space-y-2">
          {rows.map((r) => <PickupRow key={r.request_id} r={r} onDone={load} />)}
        </div>
      )}
    </div>
  );
}
